import React, { useState, useEffect } from 'react';
import {
  Button,
  message,
  Spin,
  Space,
  Tag,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  TagsOutlined,
  LeftOutlined,
  RightOutlined,
  DownOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import {
  ExpenseCategory,
  getExpenseCategories,
  deleteExpenseCategory,
} from '../../../services/expenseService';
import { AddCategoriesModalMobile } from '../../../components/expenses/AddCategoriesModalMobile';
import { NotiModal } from '../../../components/Noti/NotiModal';
import styles from './ExpenseCategoriesPageMobile.module.css';

export const ExpenseCategoriesPageMobile: React.FC = () => {
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<ExpenseCategory | undefined>();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<number | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<ExpenseCategory | undefined>();
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [filterType, setFilterType] = useState<'all' | 'active' | 'default'>('all');

  const loadCategories = async () => {
    setLoading(true);
    try {
      const categoriesData = await getExpenseCategories();
      setCategories(categoriesData);
    } catch (error: any) {
      // Hiển thị thông báo chi tiết từ API
      if (error?.response?.data?.error) {
        message.error(error.response.data.error);
      } else if (error?.message) {
        message.error(error.message);
      } else {
        message.error('Không thể tải danh mục');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCategories();
  }, []);

  const handleAddCategory = () => {
    setEditingCategory(undefined);
    setIsModalOpen(true);
  };

  const handleEditCategory = (category: ExpenseCategory) => {
    setEditingCategory(category);
    setIsModalOpen(true);
    setIsDetailModalOpen(false);
  };

  const handleDeleteCategory = async (id: number) => {
    try {
      await deleteExpenseCategory(id);
      message.success('Xóa danh mục thành công');
      loadCategories();
    } catch (error: any) {
      // Hiển thị thông báo chi tiết từ API
      if (error?.response?.data?.error) {
        message.error(error.response.data.error);
      } else if (error?.message) {
        message.error(error.message);
      } else {
        message.error('Có lỗi xảy ra khi xóa danh mục');
      }
    }
  };

  const handleShowDeleteModal = (categoryId: number) => {
    setCategoryToDelete(categoryId);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (categoryToDelete) {
      await handleDeleteCategory(categoryToDelete);
      setShowDeleteModal(false);
      setCategoryToDelete(null);
    }
  };

  const handleModalSuccess = () => {
    loadCategories();
  };

  const handleCategoryClick = (category: ExpenseCategory) => {
    setSelectedCategory(category);
    setIsDetailModalOpen(true);
  };

  const handleDetailModalEdit = () => {
    if (selectedCategory) {
      handleEditCategory(selectedCategory);
    }
  };

  const handleDetailModalDelete = () => {
    if (selectedCategory) {
      handleShowDeleteModal(selectedCategory.id);
      setIsDetailModalOpen(false);
    }
  };

  const handleFilterChange = (type: 'all' | 'active' | 'default') => {
    setFilterType(type);
    setShowFilterDropdown(false);
  };

  const getFilteredCategories = () => {
    switch (filterType) {
      case 'active':
        return categories.filter(cat => cat.is_active);
      case 'default':
        return categories.filter(cat => cat.is_default);
      default:
        return categories;
    }
  };

  const getFilterTypeText = () => {
    switch (filterType) {
      case 'active':
        return 'Danh mục hoạt động';
      case 'default':
        return 'Danh mục mặc định';
      default:
        return 'Tất cả danh mục';
    }
  };

  const getCategoryIcon = (category: ExpenseCategory) => {
    return category.icon || '📁';
  };

  const getCategoryColor = (category: ExpenseCategory) => {
    if (category.is_default) return '#ff89a1';
    if (category.is_active) return '#ff6b8a';
    return '#ff4d6a';
  };

  const getCategoryTagClass = (category: ExpenseCategory) => {
    if (category.is_default) return styles.defaultTag;
    if (category.is_active) return styles.activeTag;
    return styles.inactiveTag;
  };

  const renderCategoryCard = (category: ExpenseCategory) => {
    return (
      <div 
        key={category.id} 
        className={styles.categoryCard}
        onClick={() => handleCategoryClick(category)}
        style={{ cursor: 'pointer' }}
      >
        <div className={styles.iconContainer} style={{ backgroundColor: getCategoryColor(category) }}>
          {getCategoryIcon(category)}
        </div>
        
        <div className={styles.categoryDetails}>
          <p className={styles.categoryTitle}>
            {category.name}
          </p>
          <div className={styles.categoryTags}>
            <Space>
              <Tag color={category.is_active ? 'green' : 'red'} className={getCategoryTagClass(category)}>
                {category.is_active ? 'Hoạt động' : 'Không hoạt động'}
              </Tag>
              {category.is_default && (
                <Tag color="blue" className={styles.defaultTag}>
                  Mặc định
                </Tag>
              )}
            </Space>
          </div>
        </div>
      </div>
    );
  };

  const renderEmptyState = () => (
    <div className={styles.emptyState}>
      <div className={styles.emptyIcon}>
        <TagsOutlined />
      </div>
      <div className={styles.emptyText}>
        Chưa có danh mục chi tiêu nào
      </div>
      <div className={styles.emptySubtext}>
        Bắt đầu thêm danh mục đầu tiên của bạn
      </div>
    </div>
  );

  const filteredCategories = getFilteredCategories();

  return (
    <div className={styles.container}>
      <div className={styles.mobileShell}>
        {/* Category List */}
        <main className={styles.categoryList}>
          <div className={styles.listHeader}>
            <div className={styles.headerLeft} onClick={() => setShowFilterDropdown(!showFilterDropdown)}>
              <h3>{getFilterTypeText()}</h3>
              <DownOutlined style={{ 
                transform: showFilterDropdown ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease'
              }} />
            </div>
            <div className={styles.headerRight}>
              <div className={styles.addButton} onClick={handleAddCategory}>
                <PlusOutlined />
              </div>
            </div>
          </div>

          {/* Filter Dropdown */}
          {showFilterDropdown && (
            <div className={styles.filterDropdown}>
              <div 
                className={styles.filterOption}
                onClick={() => handleFilterChange('all')}
              >
                <span>Tất cả danh mục</span>
              </div>
              <div 
                className={styles.filterOption}
                onClick={() => handleFilterChange('active')}
              >
                <span>Danh mục hoạt động</span>
              </div>
              <div 
                className={styles.filterOption}
                onClick={() => handleFilterChange('default')}
              >
                <span>Danh mục mặc định</span>
              </div>
            </div>
          )}

          {loading ? (
            <div className={styles.loadingContainer}>
              <Spin size="large" />
            </div>
          ) : filteredCategories.length > 0 ? (
            <div className={styles.categoriesList}>
              {filteredCategories.map(renderCategoryCard)}
            </div>
          ) : (
            renderEmptyState()
          )}
        </main>
      </div>

      {/* Modal thêm/sửa danh mục */}
      <AddCategoriesModalMobile
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        category={editingCategory}
        onSuccess={handleModalSuccess}
      />

      {/* Modal chi tiết danh mục */}
      {isDetailModalOpen && selectedCategory && (
        <div className={styles.detailModal} onClick={() => setIsDetailModalOpen(false)}>
          <div className={styles.detailContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.detailHeader}>
              <div className={styles.detailIcon} style={{ backgroundColor: getCategoryColor(selectedCategory) }}>
                {getCategoryIcon(selectedCategory)}
              </div>
              <div className={styles.detailInfo}>
                <h3>{selectedCategory.name}</h3>
                <div className={styles.detailTags}>
                  <Space>
                    <Tag color={selectedCategory.is_active ? 'green' : 'red'}>
                      {selectedCategory.is_active ? 'Hoạt động' : 'Không hoạt động'}
                    </Tag>
                    {selectedCategory.is_default && (
                      <Tag color="blue">Mặc định</Tag>
                    )}
                  </Space>
                </div>
              </div>
              <Button
                type="text"
                icon={<CloseOutlined />}
                onClick={() => setIsDetailModalOpen(false)}
                className={styles.closeButton}
              />
            </div>
                         <div className={styles.detailActions}>
               <Button
                 type="primary"
                 icon={<EditOutlined />}
                 onClick={handleDetailModalEdit}
                 className={styles.detailActionButton}
                 style={{ backgroundColor: '#ff89a1', borderColor: '#ff89a1' }}
               >
                 Chỉnh sửa
               </Button>
              {!selectedCategory.is_default && (
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  onClick={handleDetailModalDelete}
                  className={styles.detailActionButton}
                >
                  Xóa
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal xác nhận xóa */}
      <NotiModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        title="Xác nhận xóa"
        message="Bạn có chắc chắn muốn xóa danh mục này?"
        confirmText="Xóa"
        cancelText="Hủy"
      />
    </div>
  );
};
